/**
 * Derivation helpers over the delegated-permission catalogue.
 *
 * The catalogue itself is **served, not stored here**: `GET
 * /app-registration-scopes` (`useAppRegistrationScopes`) returns it from
 * `backend/src/scaffold_ui/services/app_registration_catalog.py`, the same
 * table the API validates every `scope_keys` entry against. This file used to
 * carry a hand-transcribed copy of all 36 entries, which works right up until
 * the catalogue changes and a permission reads `low` on the screen while the
 * server treats it as `high` — the requester picks and the approver decides
 * from `risk` and `admin_consent_required`, so a second copy of them drifts in
 * exactly the direction nobody notices.
 *
 * What is left is pure derivation: index, group, and reduce a catalogue that
 * the caller passes in. No module-level state, so these stay trivially
 * testable and can never go stale on their own.
 */

import type { DelegatedScope } from "@/lib/api";

export type { DelegatedScope };

/** How much damage the scope can do if the app is compromised. */
export type ScopeRisk = DelegatedScope["risk"];

/** The permission family the picker files a scope under. Presentation only. */
export type ScopeGroup = DelegatedScope["group"];

/** A catalogue indexed by the `key` that gets stored on a request. */
export type ScopeIndex = ReadonlyMap<string, DelegatedScope>;

/** One picker section: a permission family and its scopes, in server order. */
export interface ScopeGroupEntry {
  group: ScopeGroup;
  scopes: DelegatedScope[];
}

/** Risk tiers from least to most dangerous, for comparing two of them. */
const RISK_ORDER: readonly ScopeRisk[] = ["low", "medium", "high"];

/**
 * Index a catalogue by scope key.
 *
 * `undefined` (still loading, or the fetch failed) indexes to an empty map
 * rather than throwing: callers render the raw key in that case, so a missing
 * catalogue degrades to something readable instead of a blank cell.
 */
export function indexScopes(scopes: readonly DelegatedScope[] | undefined): ScopeIndex {
  return new Map((scopes ?? []).map((scope) => [scope.key, scope]));
}

/**
 * Bucket a catalogue into picker sections.
 *
 * Group order and the order within each group are **the server's** —
 * `list_scopes()` returns the catalogue in picker order, least dangerous
 * first, and re-sorting it here would throw that away.
 */
export function groupScopes(scopes: readonly DelegatedScope[] | undefined): ScopeGroupEntry[] {
  const entries: ScopeGroupEntry[] = [];
  const byGroup = new Map<ScopeGroup, DelegatedScope[]>();
  for (const scope of scopes ?? []) {
    let bucket = byGroup.get(scope.group);
    if (!bucket) {
      bucket = [];
      byGroup.set(scope.group, bucket);
      entries.push({ group: scope.group, scopes: bucket });
    }
    bucket.push(scope);
  }
  return entries;
}

/** The worst risk tier among *keys*, or `null` when none of them resolve. */
export function highestRisk(keys: readonly string[], scopes: ScopeIndex): ScopeRisk | null {
  let highest: ScopeRisk | null = null;
  for (const key of keys) {
    const scope = scopes.get(key);
    if (
      scope &&
      (highest === null || RISK_ORDER.indexOf(scope.risk) > RISK_ORDER.indexOf(highest))
    ) {
      highest = scope.risk;
    }
  }
  return highest;
}

/** Whether any of *keys* needs a directory admin to consent. */
export function needsAdminConsent(keys: readonly string[], scopes: ScopeIndex): boolean {
  return keys.some((key) => scopes.get(key)?.admin_consent_required === true);
}

/** The keys that the catalogue does not know — never dropped, always shown. */
export function unrecognisedKeys(keys: readonly string[], scopes: ScopeIndex): string[] {
  return keys.filter((key) => !scopes.has(key));
}

/** What an amendment moves, in each direction. */
export interface ScopeDiff {
  added: string[];
  removed: string[];
  unchanged: string[];
}

/**
 * Compare two scope-key sets.
 *
 * Sets, not lists: the order a requester ticked permissions in is not part of
 * what a registration holds, and treating a reordering as a change would show
 * a diff where there is none — and let a no-op request past the form. The
 * server compares the same way.
 *
 * Sorted output, because both halves are read by a human deciding whether to
 * approve, and a stable order is what makes two amendments of the same
 * registration comparable.
 */
export function diffScopeKeys(base: readonly string[], target: readonly string[]): ScopeDiff {
  const baseSet = new Set(base);
  const targetSet = new Set(target);
  return {
    added: [...targetSet].filter((key) => !baseSet.has(key)).sort(),
    removed: [...baseSet].filter((key) => !targetSet.has(key)).sort(),
    unchanged: [...targetSet].filter((key) => baseSet.has(key)).sort(),
  };
}

/**
 * The OIDC identity scopes the server adds to every request and amendment.
 *
 * Mirrors `IDENTITY_SCOPE_KEYS` in `services/app_registration_catalog.py`. The
 * server is authoritative and re-adds them regardless of what is sent; this
 * copy is only the fallback for an amendment base that does not name its own
 * `locked_scope_keys` (see `lockedScopeKeysOf`).
 */
export const IDENTITY_SCOPE_KEYS: readonly string[] = [
  "graph:openid",
  "graph:profile",
  "graph:email",
];

/**
 * The permissions an amendment always keeps, as the server named them.
 *
 * The identity scopes on every registration; on a project's own sign-in
 * registration also `User.Read`, without which nobody can sign in. The server
 * re-adds them whatever is sent, so a form keeps them out of what the
 * requester edits and still shows an honest diff. Falls back to
 * `IDENTITY_SCOPE_KEYS` when the base does not say (an empty list is treated
 * the same: the identity scopes are re-added regardless).
 */
export function lockedScopeKeysOf(
  base: { locked_scope_keys?: string[] } | null | undefined,
): readonly string[] {
  const locked = base?.locked_scope_keys;
  return locked && locked.length > 0 ? locked : IDENTITY_SCOPE_KEYS;
}

/** *keys* with the *locked* scopes first, de-duplicated — the server's rule. */
export function withLockedScopes(keys: readonly string[], locked: readonly string[]): string[] {
  return [...locked, ...keys.filter((key) => !locked.includes(key))];
}

/** *keys* minus the *locked* scopes — the part a requester actually chooses. */
export function withoutLockedScopes(keys: readonly string[], locked: readonly string[]): string[] {
  return keys.filter((key) => !locked.includes(key));
}
